import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2,
  Eye,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Inbox,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toJpeg } from "html-to-image";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import {
  useContactTypes,
  useSubmitContactMessage,
  useGrantSupportAccess,
} from "../../hooks/useContactSupport";
import { useSupportTickets } from "@/hooks/useSupportTickets";
import { useAppStore } from "@/stores/appStore";
import { useProjectAuth } from "@/hooks/useProjectAuth";
import { PROJECT_ACTIONS } from "@/constants/auth";
import { getProject } from "@/services/api/sdk.gen";
import { SegmentedControl } from "../ui/segmented-control";
import { ProductionPicker, type SelectedProduction } from "./ProductionPicker";
import { routes } from "@/routes";
import type { ReportableError } from "@/types/error.types";
import { formatErrorForSupportForm } from "@/types/error.types";
import { useSupportDialogStore } from "@/stores/useSupportDialogStore";
import {
  getDiagnostics,
  type DiagnosticSnapshot,
} from "@/services/diagnosticRecorder";
import type { CreatedSupportTicket } from "@/types/support";

const MESSAGE_MAX_LENGTH = 5000;

interface ContactSupportDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional error to prefill (for standalone usage without store) */
  prefillError?: ReportableError;
  /**
   * Called after a ticket is successfully created, with the new ticket (or
   * `null` if the payload was unexpected). Lets callers navigate to the ticket.
   */
  onSubmitted?: (ticket: CreatedSupportTicket | null) => void;
}

async function capturePageSnapshot(): Promise<string | null> {
  try {
    return await toJpeg(document.body, {
      quality: 0.7,
      pixelRatio: 0.5,
      filter: (el) => {
        if (!(el instanceof HTMLElement)) return true;
        if (el.getAttribute("role") === "dialog") return false;
        if (el.hasAttribute("data-radix-portal")) return false;
        if (
          el.classList.contains("fixed") &&
          el.getAttribute("data-state") === "open"
        )
          return false;
        return true;
      },
    });
  } catch {
    // Fallback: try without filter
    try {
      return await toJpeg(document.body, {
        quality: 0.5,
        pixelRatio: 0.5,
      });
    } catch {
      return null;
    }
  }
}

/**
 * Awareness callout shown at the top of the contact form: surfaces the caller's
 * OPEN support cases (and any unread replies) and links through to the tickets
 * page, so people continue an existing conversation instead of opening a
 * duplicate. Reads open tickets via `useSupportTickets(false)`; because it is
 * only rendered inside the (unmounted-when-closed) dialog content, the query
 * runs only while the dialog is open. Renders nothing while loading.
 */
export const SupportCasesCallout: React.FC<{
  onNavigate: (path: string) => void;
}> = ({ onNavigate }) => {
  const { data: openTickets = [], isLoading } = useSupportTickets(false);

  if (isLoading) return null;

  // No open cases → render nothing. The always-visible "My Support" footer link
  // covers navigation to the page; the callout only appears to nudge you toward
  // an EXISTING open case.
  if (openTickets.length === 0) return null;

  const unreadTotal = openTickets.reduce(
    (sum, t) => sum + (t.unread_count ?? 0),
    0,
  );
  const single = openTickets.length === 1 ? openTickets[0] : null;
  const target = single ? routes.supportTicket(single.id) : routes.support();

  return (
    <button
      type="button"
      onClick={() => onNavigate(target)}
      className="flex w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.06]"
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5">
          <Inbox className="h-4 w-4 text-muted-foreground" />
          {unreadTotal > 0 && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-background" />
          )}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">
            {single
              ? `You have an open case · ${single.ticket_number}`
              : `You have ${openTickets.length} open cases`}
          </p>
          <p className="truncate text-xs text-foreground/70">
            {unreadTotal > 0
              ? `${unreadTotal} unread ${unreadTotal === 1 ? "reply" : "replies"}`
              : "Continue there instead of a new case"}
          </p>
        </div>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
        {single ? "View case" : "View cases"}
        <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
};

/**
 * ContactSupportDialog Component
 *
 * Can be used in two ways:
 * 1. Controlled mode: Pass `open` and `onOpenChange` props
 * 2. Store mode: Use `useSupportDialogStore` to control open state and prefill errors
 *
 * For the global instance, use `ContactSupportDialogGlobal` which connects to the store.
 */
export const ContactSupportDialog: React.FC<ContactSupportDialogProps> = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  prefillError: controlledPrefillError,
  onSubmitted,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contactTypeId, setContactTypeId] = useState<string>("");
  const [message, setMessage] = useState("");
  const [includeSnapshot, setIncludeSnapshot] = useState(false);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(
    null,
  );
  const [isCapturing, setIsCapturing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showErrorContext, setShowErrorContext] = useState(false);
  const [includeDiagnostics, setIncludeDiagnostics] = useState(false);
  const [diagnosticsSnapshot, setDiagnosticsSnapshot] =
    useState<DiagnosticSnapshot | null>(null);
  const [showDiagnosticsPreview, setShowDiagnosticsPreview] = useState(false);
  // Phase 5 consent: let the customer allow the support agent to view the
  // production this ticket is about. Default OFF; only offered once a
  // production is selected (see the scope block below).
  const [allowSupportAccess, setAllowSupportAccess] = useState(false);

  // Ticket scope: is this ticket about a specific production? When yes, the
  // customer picks (or keeps the pre-filled current) production via
  // `ProductionPicker`; that production is bound to the ticket and gates the
  // consent option.
  const [isAboutProduction, setIsAboutProduction] = useState(false);
  const [selectedProduction, setSelectedProduction] =
    useState<SelectedProduction | null>(null);

  // Get store state for store mode
  const {
    isOpen: storeIsOpen,
    prefillError: storePrefillError,
    close: storeClose,
    clearPrefillError,
  } = useSupportDialogStore();

  // Determine if we're in controlled mode or store mode
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : storeIsOpen;
  const prefillError = isControlled
    ? controlledPrefillError
    : storePrefillError;

  const { data: contactTypes } = useContactTypes(open);
  const submitMutation = useSubmitContactMessage();
  const grantAccessMutation = useGrantSupportAccess();

  // Resolve the production the customer is currently in. When present it
  // pre-fills the scope block (segmented control defaults to "Yes" and the
  // picker to this production); otherwise the scope defaults to "No".
  const currentProject = useAppStore((state) => state.currentProject);

  // Initialise the scope block on the dialog's rising edge (closed -> open),
  // so reopening always reflects the current production without clobbering the
  // customer's in-session choices.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      if (currentProject) {
        setIsAboutProduction(true);
        setSelectedProduction({
          id: currentProject.id,
          name: currentProject.name,
          auth: currentProject.auth,
        });
      } else {
        setIsAboutProduction(false);
        setSelectedProduction(null);
      }
    }
    wasOpen.current = open ?? false;
  }, [open, currentProject]);

  // The ticket is production-scoped only when the customer said "Yes" AND has
  // actually picked a production. "Yes" with nothing picked is treated as "No".
  const hasProductionScope = isAboutProduction && selectedProduction !== null;

  // Consent gate: a customer may only grant support access to a production they
  // can share (editor/owner). Search-selected productions carry their `auth`
  // inline; the pre-filled current production may not, so fetch it on demand.
  const needsAuthFetch = Boolean(
    selectedProduction && !selectedProduction.auth,
  );
  const { data: fetchedAuth } = useQuery({
    queryKey: ["support-production-auth", selectedProduction?.id],
    queryFn: async () => {
      const response = await getProject({
        path: { projectId: selectedProduction!.id },
      });
      return response.data?.data?.auth ?? {};
    },
    enabled: needsAuthFetch,
    staleTime: 5 * 60 * 1000,
  });
  const productionAuth = selectedProduction?.auth ?? fetchedAuth;
  const { can } = useProjectAuth(productionAuth);
  const canShareProduction = can(PROJECT_ACTIONS.SHARE);

  // When a production is selected AND the caller can grant on it (editor/owner),
  // default the support-access consent to ON. Applied ONCE per selected
  // production id (via the ref) so a later manual uncheck sticks and switching
  // productions re-defaults; a production the caller can't share never
  // auto-checks (its checkbox is disabled). handleProductionChange resets the
  // checkbox to OFF first, so this only ever turns it ON for a shareable pick.
  const consentDefaultedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedProduction) {
      consentDefaultedFor.current = null;
      return;
    }
    if (
      consentDefaultedFor.current !== selectedProduction.id &&
      canShareProduction
    ) {
      setAllowSupportAccess(true);
      consentDefaultedFor.current = selectedProduction.id;
    }
  }, [selectedProduction, canShareProduction]);

  // Find the "bug-report" contact type ID
  const bugReportTypeId = useMemo(() => {
    const bugType = contactTypes?.find(
      (type) => type.slug === "bug-report" || type.slug.includes("bug"),
    );
    return bugType ? String(bugType.id) : null;
  }, [contactTypes]);

  // Apply prefill when error is provided and dialog opens
  useEffect(() => {
    if (open && prefillError && contactTypes) {
      // Auto-select bug report type if available
      if (bugReportTypeId && !contactTypeId) {
        setContactTypeId(bugReportTypeId);
      }

      // Pre-fill message with formatted error if message is empty
      if (!message) {
        setMessage(formatErrorForSupportForm(prefillError));
      }
    }
  }, [
    open,
    prefillError,
    contactTypes,
    bugReportTypeId,
    contactTypeId,
    message,
  ]);

  const handleSnapshotToggle = useCallback(
    async (checked: boolean) => {
      setIncludeSnapshot(checked);
      if (checked && !screenshotDataUrl) {
        setIsCapturing(true);
        const dataUrl = await capturePageSnapshot();
        setScreenshotDataUrl(dataUrl);
        setIsCapturing(false);
      }
    },
    [screenshotDataUrl],
  );

  const handleDiagnosticsToggle = useCallback((checked: boolean) => {
    setIncludeDiagnostics(checked);
    // Freeze a snapshot at toggle time so the preview matches what will be sent
    setDiagnosticsSnapshot(checked ? getDiagnostics() : null);
    if (!checked) setShowDiagnosticsPreview(false);
  }, []);

  const diagnosticsPreviewText = useMemo(() => {
    if (!diagnosticsSnapshot) return "";
    return JSON.stringify(diagnosticsSnapshot, null, 2);
  }, [diagnosticsSnapshot]);

  const resetForm = useCallback(() => {
    setContactTypeId("");
    setMessage("");
    setIncludeSnapshot(false);
    setScreenshotDataUrl(null);
    setIsCapturing(false);
    setShowPreview(false);
    setShowErrorContext(false);
    setIncludeDiagnostics(false);
    setDiagnosticsSnapshot(null);
    setShowDiagnosticsPreview(false);
    setAllowSupportAccess(false);
    setIsAboutProduction(false);
    setSelectedProduction(null);
    // Allow the rising-edge effect to re-initialise the scope on next open.
    wasOpen.current = false;
    // Clear prefill error from store
    if (!isControlled) {
      clearPrefillError();
    }
  }, [isControlled, clearPrefillError]);

  // Toggle the "about a specific production?" scope. Switching to "No" clears
  // any bound production and the consent it gated.
  const handleScopeChange = useCallback((next: string) => {
    const about = next === "yes";
    setIsAboutProduction(about);
    if (!about) {
      setSelectedProduction(null);
      setAllowSupportAccess(false);
    }
  }, []);

  // When the picked production changes, drop stale consent (it referenced the
  // previous production).
  const handleProductionChange = useCallback(
    (production: SelectedProduction | null) => {
      setSelectedProduction(production);
      setAllowSupportAccess(false);
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!contactTypeId || !message.trim()) return;

    try {
      const body: {
        contactTypeId: number;
        message: string;
        screenshotBase64?: string;
        pageHtml?: string;
        pageUrl?: string;
        diagnostics?: DiagnosticSnapshot;
        aboutProjectId?: string;
        errorContext?: {
          correlationId?: string;
          eventId?: string;
          code?: string;
          tk?: string;
          timestamp?: string;
          workflowId?: string;
          executionId?: string;
          nodeId?: string;
          projectId?: string;
        };
      } = {
        contactTypeId: Number(contactTypeId),
        message: message.trim(),
      };

      // Bind the ticket to the picked production whenever the customer scoped
      // it to one, so the created ticket is production-scoped and a consent
      // grant has a concrete production to reference.
      if (hasProductionScope && selectedProduction) {
        body.aboutProjectId = selectedProduction.id;
      }

      if (includeDiagnostics && diagnosticsSnapshot) {
        body.diagnostics = diagnosticsSnapshot;
      }

      if (includeSnapshot) {
        body.pageUrl = window.location.href;
      }

      if (includeSnapshot && screenshotDataUrl) {
        body.screenshotBase64 = screenshotDataUrl.replace(
          /^data:image\/\w+;base64,/,
          "",
        );
        // Capture sanitised DOM snapshot (strip scripts, sensitive elements, input values)
        const clone = document.documentElement.cloneNode(true) as HTMLElement;
        clone
          .querySelectorAll(
            "script, [data-token], [data-secret], [data-sensitive]",
          )
          .forEach((el) => el.remove());
        clone
          .querySelectorAll('input[type="password"]')
          .forEach((el) => el.remove());
        clone.querySelectorAll("input, textarea").forEach((el) => {
          el.setAttribute("value", "");
          (el as HTMLTextAreaElement).textContent = "";
        });
        const html = clone.outerHTML;
        if (html.length <= 5 * 1024 * 1024) {
          body.pageHtml = html;
        }
      }

      // Include error context if available
      if (prefillError) {
        body.errorContext = {
          correlationId: prefillError.correlationId,
          eventId: prefillError.eventId,
          code: prefillError.code,
          tk: prefillError.tk,
          timestamp: prefillError.timestamp,
          workflowId: prefillError.workflowId,
          executionId: prefillError.executionId,
          nodeId: prefillError.nodeId,
          projectId: prefillError.projectId,
        };
      }

      const envelope = await submitMutation.mutateAsync(body);
      const createdTicketId = envelope?.data?.id;

      // If the customer consented, grant the support agent access to the
      // production now that we have the created ticket id. This is best-effort:
      // a failure here is NON-FATAL (the ticket still exists), so we surface a
      // toast and continue rather than failing the whole submission.
      if (hasProductionScope && allowSupportAccess && createdTicketId) {
        try {
          await grantAccessMutation.mutateAsync({ ticketId: createdTicketId });
        } catch {
          toast.error(
            "Your message was sent, but we couldn't grant support access to this production. You can grant it later from the production's Share dialog.",
          );
        }
      }

      toast.success("Message sent! We'll get back to you soon.", {
        // Close the loop: jump straight to the new case rather than dismissing.
        action: createdTicketId
          ? {
              label: "View case",
              onClick: () => navigate(routes.supportTicket(createdTicketId)),
            }
          : undefined,
      });
      resetForm();
      onSubmitted?.(envelope?.data ?? null);
      if (isControlled) {
        controlledOnOpenChange?.(false);
      } else {
        storeClose();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to send message. Please try again.";
      toast.error(errorMessage);
    }
  }, [
    contactTypeId,
    message,
    includeSnapshot,
    screenshotDataUrl,
    includeDiagnostics,
    diagnosticsSnapshot,
    prefillError,
    submitMutation,
    resetForm,
    onSubmitted,
    isControlled,
    controlledOnOpenChange,
    storeClose,
    navigate,
    hasProductionScope,
    selectedProduction,
    allowSupportAccess,
    grantAccessMutation,
  ]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetForm();
        submitMutation.reset();
      }
      if (isControlled) {
        controlledOnOpenChange?.(nextOpen);
      } else {
        if (nextOpen) {
          // Store handles opening via useSupportDialogStore.open()
        } else {
          storeClose();
        }
      }
    },
    [
      isControlled,
      controlledOnOpenChange,
      resetForm,
      submitMutation,
      storeClose,
    ],
  );

  // Close the dialog (discarding the draft, same as Cancel) and route to the
  // support tickets page / a specific case. Used by the open-cases callout.
  const closeAndNavigate = useCallback(
    (path: string) => {
      handleOpenChange(false);
      navigate(path);
    },
    [handleOpenChange, navigate],
  );

  const isSendDisabled =
    !contactTypeId ||
    !message.trim() ||
    submitMutation.isPending ||
    (includeSnapshot && isCapturing);

  // Build error context display items
  const errorContextItems = useMemo(() => {
    if (!prefillError) return [];
    const items: { label: string; value: string }[] = [];
    if (prefillError.correlationId) {
      items.push({
        label: "Correlation ID",
        value: prefillError.correlationId,
      });
    }
    if (prefillError.eventId) {
      items.push({ label: "Event ID", value: prefillError.eventId });
    }
    if (prefillError.code) {
      items.push({ label: "Error Code", value: prefillError.code });
    }
    if (prefillError.workflowId) {
      items.push({ label: "Workflow", value: prefillError.workflowId });
    }
    if (prefillError.executionId) {
      items.push({ label: "Execution", value: prefillError.executionId });
    }
    if (prefillError.nodeId) {
      items.push({ label: "Node", value: prefillError.nodeId });
    }
    if (prefillError.projectId) {
      items.push({ label: "Project", value: prefillError.projectId });
    }
    return items;
  }, [prefillError]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0" hideCloseButton>
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              {prefillError ? "Report an Error" : "Contact Support"}
            </DialogTitle>
            <DialogDescription>
              {prefillError
                ? "We're sorry you encountered an error. The details below will help us investigate and fix the issue."
                : "Have a question or need help? Send us a message and we will get back to you as soon as possible."}
            </DialogDescription>
          </DialogHeader>

          <div
            className="px-6 py-5"
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {/* Existing open cases + a link to the tickets page. Hidden in the
                focused error-report flow. */}
            {!prefillError && (
              <SupportCasesCallout onNavigate={closeAndNavigate} />
            )}

            {/* Error Context Collapsible */}
            {prefillError && errorContextItems.length > 0 && (
              <Collapsible
                open={showErrorContext}
                onOpenChange={setShowErrorContext}
              >
                <CollapsibleTrigger
                  type="button"
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-red-950/20 border border-red-800/50 text-left transition-colors hover:bg-red-950/30"
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-400" />
                    <span className="text-sm font-medium text-red-100">
                      Error Context
                    </span>
                    <span className="text-xs text-red-400">
                      ({errorContextItems.length} details attached)
                    </span>
                  </div>
                  {showErrorContext ? (
                    <ChevronUp className="h-4 w-4 text-red-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-red-400" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="rounded-lg bg-muted/50 border p-3 space-y-1.5">
                    {errorContextItems.map((item) => (
                      <div
                        key={item.label}
                        className="flex justify-between text-xs"
                      >
                        <span className="text-muted-foreground">
                          {item.label}:
                        </span>
                        <span className="font-mono text-foreground truncate max-w-[200px]">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Scope block: is this ticket about a specific production? When
                yes, pick (or keep the pre-filled current) production and,
                once selected, offer the support-access consent. */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                  Is this about a specific production?
                </Label>
                <SegmentedControl
                  aria-label="Is this about a specific production?"
                  value={isAboutProduction ? "yes" : "no"}
                  onChange={handleScopeChange}
                  options={[
                    { label: "No", value: "no" },
                    { label: "Yes", value: "yes" },
                  ]}
                />
              </div>

              {isAboutProduction && (
                <ProductionPicker
                  value={selectedProduction}
                  onChange={handleProductionChange}
                />
              )}

              {hasProductionScope && selectedProduction && (
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="allow-support-access"
                      checked={allowSupportAccess}
                      disabled={!canShareProduction}
                      onCheckedChange={(checked) =>
                        setAllowSupportAccess(checked === true)
                      }
                    />
                    <Label
                      htmlFor="allow-support-access"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Allow the support agent to view the selected production
                      while this ticket is open.
                    </Label>
                  </div>
                  {!canShareProduction && (
                    <p className="text-xs text-muted-foreground pl-6">
                      Only an editor or owner of this production can grant
                      support access.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="contact-type"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                How can we help?
              </Label>
              <Select value={contactTypeId} onValueChange={setContactTypeId}>
                <SelectTrigger
                  id="contact-type"
                  style={{
                    height: "36px",
                    borderRadius: "8px",
                    backgroundColor: "var(--color-background)",
                  }}
                >
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {contactTypes?.map((type) => (
                    <SelectItem key={type.id} value={String(type.id)}>
                      {t(type.tk, { defaultValue: type.slug })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="contact-message"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                Your message
              </Label>
              <Textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  prefillError
                    ? "Describe what you were doing when this error occurred..."
                    : "Describe your issue or feedback..."
                }
                maxLength={MESSAGE_MAX_LENGTH}
                rows={6}
                style={{
                  borderRadius: "8px",
                  backgroundColor: "var(--color-background)",
                }}
              />
              <p className="text-xs text-muted-foreground text-right">
                {message.length}/{MESSAGE_MAX_LENGTH}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="include-snapshot"
                checked={includeSnapshot}
                onCheckedChange={(checked) =>
                  void handleSnapshotToggle(checked === true)
                }
                disabled={isCapturing}
              />
              <Label
                htmlFor="include-snapshot"
                className="text-sm font-normal cursor-pointer"
              >
                {isCapturing
                  ? "Capturing snapshot..."
                  : "Include page snapshot"}
              </Label>
              {screenshotDataUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setShowPreview(true)}
                >
                  <Eye className="w-3.5 h-3.5 mr-1" />
                  Preview
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="include-diagnostics"
                checked={includeDiagnostics}
                onCheckedChange={(checked) =>
                  handleDiagnosticsToggle(checked === true)
                }
              />
              <Label
                htmlFor="include-diagnostics"
                className="text-sm font-normal cursor-pointer"
              >
                Include diagnostics (recent console + network activity)
              </Label>
              {diagnosticsSnapshot && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setShowDiagnosticsPreview(true)}
                >
                  <Eye className="w-3.5 h-3.5 mr-1" />
                  Preview ({diagnosticsSnapshot.entryCount})
                </Button>
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t gap-2">
            {/* Always-visible link to the support page (pinned left via mr-auto,
                so Cancel + Send stay right). */}
            <button
              type="button"
              onClick={() => closeAndNavigate(routes.support())}
              className="mr-auto inline-flex items-center gap-1.5 self-center text-xs text-foreground/70 transition-colors hover:text-foreground"
            >
              <Inbox className="h-3.5 w-3.5" />
              My Support
            </button>
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              disabled={submitMutation.isPending}
              className="inline-flex items-center justify-center font-medium"
              style={{
                height: "32px",
                padding: "0 14px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.15s ease",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                color: "#999",
                opacity: submitMutation.isPending ? 0.4 : 1,
                cursor: submitMutation.isPending ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!submitMutation.isPending) {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.color = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                if (!submitMutation.isPending) {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.12)";
                  e.currentTarget.style.color = "#999";
                }
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="support-submit-button"
              onClick={handleSubmit}
              disabled={isSendDisabled}
              className="inline-flex items-center justify-center font-medium"
              style={{
                height: "32px",
                padding: "0 14px",
                gap: "6px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.15s ease",
                background: "rgba(41, 120, 196, 0.15)",
                border: "1px solid rgba(41, 120, 196, 0.4)",
                color: "#7fb8e8",
                opacity: isSendDisabled ? 0.4 : 1,
                cursor: isSendDisabled ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isSendDisabled) {
                  e.currentTarget.style.background = "rgba(41, 120, 196, 0.25)";
                  e.currentTarget.style.borderColor = "rgba(41, 120, 196, 0.6)";
                  e.currentTarget.style.color = "#93c5fd";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSendDisabled) {
                  e.currentTarget.style.background = "rgba(41, 120, 196, 0.15)";
                  e.currentTarget.style.borderColor = "rgba(41, 120, 196, 0.4)";
                  e.currentTarget.style.color = "#7fb8e8";
                }
              }}
            >
              {submitMutation.isPending && (
                <Loader2
                  className="animate-spin"
                  style={{ width: "18px", height: "18px" }}
                />
              )}
              {prefillError ? "Report Error" : "Send Message"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[720px]">
          <DialogHeader>
            <DialogTitle>Page Snapshot Preview</DialogTitle>
          </DialogHeader>
          {screenshotDataUrl && (
            <img
              src={screenshotDataUrl}
              alt="Page snapshot"
              className="w-full rounded border"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDiagnosticsPreview}
        onOpenChange={setShowDiagnosticsPreview}
      >
        <DialogContent className="max-w-[820px]">
          <DialogHeader>
            <DialogTitle>Diagnostics Preview</DialogTitle>
            <DialogDescription>
              This is exactly what will be attached to your bug report. Console
              messages and network metadata only — no request or response
              bodies, no headers except correlation IDs, no input values.
            </DialogDescription>
          </DialogHeader>
          <pre className="text-xs font-mono whitespace-pre-wrap bg-muted/50 border rounded p-3 max-h-[60vh] overflow-auto select-text">
            {diagnosticsPreviewText}
          </pre>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDiagnosticsPreview(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Global ContactSupportDialog that connects to the store.
 * Place this once at the app root level.
 */
export const ContactSupportDialogGlobal: React.FC = () => {
  const { isOpen, prefillError, close, clearPrefillError } =
    useSupportDialogStore();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        close();
        clearPrefillError();
      }
    },
    [close, clearPrefillError],
  );

  return (
    <ContactSupportDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      prefillError={prefillError ?? undefined}
    />
  );
};
